/**
 * The walkway door's angle over time, from where the man using it is.
 *
 * Pure: no Three, no Rapier. Beat 2 steps it in the fixed step, because its
 * leaf is a collider she squeezes past (D-044); beat 1 steps it on the render
 * step, because there it is only a picture (its collider stays shut, and
 * `born-regions.spec.ts` is why that is safe: she never leaves the pen).
 */

import { WALKWAY_DOOR_AJAR, WALKWAY_DOOR_AT, WALKWAY_DOOR_OPEN } from '@/world/farrowing';

/**
 * How near the doorway a walking man has to be for it to stand open.
 *
 * Both workers park 1.08 m from the doorway's centre, so the door opens as he
 * sets off and closes once he stops there; on the hall side he is through the
 * doorway after 0.45 m and clear of this after 1.4. It is not a sensor in the
 * fiction: he opens it with his hand, and this is how far that takes.
 */
export const DOOR_NEAR = 1.4;

/**
 * Exponential rate toward the target angle, per second. The pen gate's is
 * 3.3; this is a lighter leaf on a closer, and the same order is what keeps
 * one hall's two hinges from moving at two different speeds.
 */
const DOOR_SWING_RATE = 3;

export interface DoorUser {
  x: number;
  z: number;
  /** Walking. A man standing still beside a door does not hold it open. */
  moving: boolean;
}

export class DoorSwing {
  #angle = 0;
  #rest = 0;
  readonly #ajarAfterPass: boolean;

  /**
   * `ajarAfterPass`: once he has been through it, it no longer swings shut,
   * it stops at `WALKWAY_DOOR_AJAR`. Beat 2's door, the gap she leaves by.
   */
  constructor(ajarAfterPass: boolean) {
    this.#ajarAfterPass = ajarAfterPass;
  }

  /** The leaf's angle: 0 shut, positive into the vestibule. */
  get angle(): number {
    return this.#angle;
  }

  /**
   * `blocked`, when given, is asked about the angle the leaf would move to;
   * if it says yes the leaf holds where it is this step. Beat 2 passes her
   * capsule (Codex review of step 3): a collider set into her moves her
   * however the solver likes. Checking only the destination is enough because
   * the leaf moves at most 0.08 rad a 60 Hz step, 7 cm at its free edge,
   * less than her 0.125 m radius, so it cannot step over her.
   */
  step(dt: number, user: DoorUser | null, blocked?: (angle: number) => boolean): number {
    const step = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
    const near = user !== null && user.moving
      && Math.hypot(user.x - WALKWAY_DOOR_AT.x, user.z - WALKWAY_DOOR_AT.z) < DOOR_NEAR;
    if (near && this.#ajarAfterPass) this.#rest = WALKWAY_DOOR_AJAR;
    const target = near ? WALKWAY_DOOR_OPEN : this.#rest;
    let next = this.#angle + (target - this.#angle) * (1 - Math.exp(-DOOR_SWING_RATE * step));
    if (Math.abs(target - next) < 0.001) next = target;
    if (next !== this.#angle && blocked?.(next) === true) return this.#angle;
    this.#angle = next;
    return this.#angle;
  }
}
